import { inject } from '@angular/core';
import { GlobalSearchService } from '@core/services';
import { GenerateImageUrlWithToken, GetTeamId, removeNullAndUndefinedProperties } from '@core/utils';
import { PermissionService } from '@auth';
import { plainToInstance } from 'class-transformer';
import { map } from 'rxjs';
import { GlobalSearchEntity } from '@core/entities/global-search.entity';
import { SectionType } from '@core/enums';
import { SafeUrl } from '@angular/platform-browser';

export class UseGlobalSearch {
  private readonly _globalSearchService = inject(GlobalSearchService);
  private readonly _permissionService = inject(PermissionService);

  find(query: string) {
    const teamId = +GetTeamId();
    const coursePermission = this._permissionService.isCourseAccessAllowed() ? 1 : 0;

    return this._globalSearchService.find(query, teamId, coursePermission).pipe(
      map((response: any) => {
        const transformedEntity = plainToInstance(GlobalSearchEntity, {
          courses: response.find((item: any) => item.courses)?.courses || [],
          news: response.find((item: any) => item.news)?.news || [],
          groups: response.find((item: any) => item.groups)?.groups || [],
          faq: response.find((item: any) => item.faq)?.faq || [],
          event: response.find((item: any) => item.event)?.event || [],
          task: response.find((item: any) => item.task)?.task || [],
          survey: response.find((item: any) => item.survey)?.survey || [],
          instructor: response.find((item: any) => item.instructor)?.instructor || [],
          room: response.find((item: any) => item.room)?.room || [],
        });

        // Function to remove null/undefined properties and generate image URLs
        const processEntity = <T extends { imageUrl?: SafeUrl | string }>(items: Partial<T>[], sectionType: SectionType) => {
          if (!items || !items.length) return undefined;
          return items.map((item) => {
            const cleanedItem = removeNullAndUndefinedProperties(item);
            if (cleanedItem.id) {
              cleanedItem.imageUrl = GenerateImageUrlWithToken(cleanedItem.id, sectionType);
            }
            return cleanedItem;
          });
        };

        transformedEntity.courses = processEntity(transformedEntity.courses, SectionType.COURSE);
        transformedEntity.news = processEntity(transformedEntity.news, SectionType.NEWS);
        transformedEntity.groups = processEntity(transformedEntity.groups, SectionType.GROUP);
        transformedEntity.faqs = processEntity(transformedEntity.faqs, SectionType.FAQ);
        transformedEntity.events = processEntity(transformedEntity.events, SectionType.EVENT);
        transformedEntity.tasks = processEntity(transformedEntity.tasks, SectionType.TASK);
        transformedEntity.surveys = processEntity(transformedEntity.surveys, SectionType.SURVEY);
        transformedEntity.instructors = processEntity(transformedEntity.instructors, SectionType.INSTRUCTOR);
        transformedEntity.rooms = processEntity(transformedEntity.rooms, SectionType.ROOM);

        return removeNullAndUndefinedProperties(transformedEntity);
      }),
    );
  }
}
